/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import React from 'react';
import { Dialog as Ui5Dialog, DialogDomRef, DialogPropTypes } from '@ui5/webcomponents-react';
import { useDialogStyles } from './DialogStyles';

export interface DialogProps {
    footer?: DialogPropTypes['footer'];
    header?: DialogPropTypes['header'];
    children?: DialogPropTypes['children'];
    style?: DialogPropTypes['style'];
    open?: DialogPropTypes['open'];
    headerText?: DialogPropTypes['headerText'];
    className?: DialogPropTypes['className'];
    resizable?: boolean;
    isWithPadding?: boolean;
    accessibleName?: DialogPropTypes['accessibleName'];
    dataTestId?: string;
    onBeforeClose?: DialogPropTypes['onBeforeClose'];
    onAfterClose?: DialogPropTypes['onClose'];
    onAfterOpen?: DialogPropTypes['onOpen'];
    stretch?: boolean;
    messageType?: DialogPropTypes['state'];
}

export const Dialog = React.forwardRef<DialogDomRef, DialogProps>(
    (
        {
            footer,
            header,
            children,
            style,
            open,
            headerText,
            className,
            resizable,
            isWithPadding = true,
            accessibleName,
            dataTestId,
            onBeforeClose,
            onAfterClose,
            onAfterOpen,
            stretch,
            messageType,
        },
        ref
    ) => {
        const dialogClasses = useDialogStyles();

        /* istanbul ignore next */
        const resolveClassName = (): string | undefined => {
            if (!isWithPadding) {
                return `${className || ''} ${dialogClasses.dialogWithoutContentPadding}`;
            }

            return className;
        };

        return (
            <Ui5Dialog
                ref={ref}
                data-testid={dataTestId || 'dialog'}
                style={style}
                header={header}
                footer={footer}
                open={open}
                headerText={headerText}
                className={resolveClassName()}
                resizable={resizable}
                stretch={stretch}
                data-ui5-compact-size
                onBeforeClose={onBeforeClose}
                onClose={onAfterClose}
                onOpen={onAfterOpen}
                accessibleName={accessibleName || headerText}
                state={messageType}
            >
                {children}
            </Ui5Dialog>
        );
    }
);
Dialog.displayName = 'Dialog';
